import { useState, useRef, useEffect } from 'react';
import { Type, PenTool, RotateCcw } from 'lucide-react';

/**
 * E-Signature capture component supporting both typed and drawn signatures
 *
 * @param {Object} props
 * @param {Function} props.onChange - Called with { type: 'typed'|'drawn', data: string }
 * @param {string} props.signerName - Pre-fill for typed signature
 * @param {string} props.className - Additional CSS classes
 */
export default function SignatureCapture({ onChange, signerName = '', className = '' }) {
  const [mode, setMode] = useState('typed'); // 'typed' or 'drawn'
  const [typedSignature, setTypedSignature] = useState(signerName);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Initialize canvas
  useEffect(() => {
    if (mode === 'drawn' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas size based on container
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = 150;

      // Style the canvas
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Draw signature line
      ctx.beginPath();
      ctx.strokeStyle = '#e5e5e5';
      ctx.moveTo(20, canvas.height - 30);
      ctx.lineTo(canvas.width - 20, canvas.height - 30);
      ctx.stroke();
      ctx.strokeStyle = '#1a1a1a';
    }
  }, [mode]);

  // Update parent when signature changes
  useEffect(() => {
    if (mode === 'typed') {
      onChange({
        type: 'typed',
        data: typedSignature,
        isValid: typedSignature.trim().length >= 2
      });
    }
  }, [mode, typedSignature, onChange]);

  // Get canvas coordinates from mouse/touch event
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      setIsDrawing(false);
      // Export as base64 PNG
      const data = canvasRef.current.toDataURL('image/png');
      onChange({
        type: 'drawn',
        data: data,
        isValid: hasDrawn
      });
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Clear and redraw background
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Redraw signature line
    ctx.beginPath();
    ctx.strokeStyle = '#e5e5e5';
    ctx.moveTo(20, canvas.height - 30);
    ctx.lineTo(canvas.width - 20, canvas.height - 30);
    ctx.stroke();
    ctx.strokeStyle = '#1a1a1a';

    setHasDrawn(false);
    onChange({
      type: 'drawn',
      data: '',
      isValid: false
    });
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Mode Toggle */}
      <div className="flex border border-neutral-200 dark:border-neutral-600 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setMode('typed')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
            mode === 'typed'
              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-r border-primary-200 dark:border-primary-800'
              : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 border-r border-neutral-200 dark:border-neutral-600'
          }`}
        >
          <Type className="w-4 h-4" />
          Type Signature
        </button>
        <button
          type="button"
          onClick={() => setMode('drawn')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
            mode === 'drawn'
              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
          }`}
        >
          <PenTool className="w-4 h-4" />
          Draw Signature
        </button>
      </div>

      {/* Typed Signature */}
      {mode === 'typed' && (
        <div className="space-y-2">
          <input
            type="text"
            value={typedSignature}
            onChange={(e) => setTypedSignature(e.target.value)}
            placeholder="Type your full name"
            className="w-full px-4 py-3 text-xl border border-neutral-200 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}
          />
          {typedSignature && (
            <div className="p-4 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">Signature Preview:</p>
              <p
                className="text-3xl text-neutral-800 dark:text-neutral-200"
                style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}
              >
                {typedSignature}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Drawn Signature */}
      {mode === 'drawn' && (
        <div className="space-y-2">
          <div className="relative border border-neutral-200 dark:border-neutral-600 rounded-lg overflow-hidden">
            <canvas
              ref={canvasRef}
              className="w-full cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-neutral-400 dark:text-neutral-500 text-sm">Draw your signature above the line</p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={clearCanvas}
            className="flex items-center gap-1.5 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200"
          >
            <RotateCcw className="w-4 h-4" />
            Clear & Redraw
          </button>
        </div>
      )}

      {/* Legal notice */}
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        By signing above, you agree that this electronic signature is the legal equivalent of your manual signature.
      </p>
    </div>
  );
}
